export interface Auth0Config {
  domain: string;
  clientId: string;
  audience?: string;
  redirectUri: string;
  logoutReturnTo: string;
}

export type Auth0ConfigIssueLevel = 'error' | 'warning';

export interface Auth0ConfigIssue {
  level: Auth0ConfigIssueLevel;
  envKey?: string;
  message: string;
}

export interface Auth0ConfigValidationResult {
  config: Auth0Config | null;
  issues: Auth0ConfigIssue[];
}

export interface Auth0ConfigValidationOptions {
  env?: Record<string, unknown>;
  origin?: string;
}

const getRuntimeEnv = (): Record<string, unknown> =>
  ((import.meta as ImportMeta & { env?: Record<string, unknown> }).env) ?? {};

const getRuntimeOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

const stripTrailingSlash = (value: string): string =>
  value.endsWith('/') ? value.slice(0, -1) : value;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const readEnvValue = (env: Record<string, unknown>, key: string): string | undefined => {
  const raw = env[key];
  if (!isNonEmptyString(raw)) {
    return undefined;
  }
  return raw.trim();
};

const AUTH0_CALLBACK_PATH = '/api/auth/auth0/callback';

const buildDefaultRedirectUri = (origin: string): string =>
  `${stripTrailingSlash(origin)}${AUTH0_CALLBACK_PATH}`;

const validateAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

export const validateAuth0Config = (
  options: Auth0ConfigValidationOptions = {},
): Auth0ConfigValidationResult => {
  const env = options.env ?? getRuntimeEnv();
  const origin = options.origin ?? getRuntimeOrigin();

  const domain = readEnvValue(env, 'VITE_AUTH0_DOMAIN');
  const clientId = readEnvValue(env, 'VITE_AUTH0_CLIENT_ID');
  const audience = readEnvValue(env, 'VITE_AUTH0_AUDIENCE');
  const redirectOverride = readEnvValue(env, 'VITE_AUTH0_CALLBACK_URL');
  const logoutOverride = readEnvValue(env, 'VITE_AUTH0_LOGOUT_URL');

  const issues: Auth0ConfigIssue[] = [];

  if (!domain) {
    issues.push({
      level: 'error',
      envKey: 'VITE_AUTH0_DOMAIN',
      message:
        'Set VITE_AUTH0_DOMAIN (frontend) and AUTH0_DOMAIN (backend) to your Auth0 tenant domain (e.g., dev-1234.us.auth0.com).',
    });
  } else if (/^https?:\/\//i.test(domain)) {
    issues.push({
      level: 'error',
      envKey: 'VITE_AUTH0_DOMAIN',
      message:
        'Remove the protocol (https://) from VITE_AUTH0_DOMAIN. Provide only the hostname (e.g., dev-1234.us.auth0.com).',
    });
  }

  if (!clientId) {
    issues.push({
      level: 'error',
      envKey: 'VITE_AUTH0_CLIENT_ID',
      message:
        'Set VITE_AUTH0_CLIENT_ID (frontend) and AUTH0_CLIENT_ID (backend) to the SPA application client ID.',
    });
  }

  const redirectUri = redirectOverride ?? buildDefaultRedirectUri(origin);
  if (!redirectOverride) {
    issues.push({
      level: 'warning',
      envKey: 'VITE_AUTH0_CALLBACK_URL',
      message: `No explicit callback URL configured; falling back to ${redirectUri}. Register this URL in Auth0 and update both VITE_AUTH0_CALLBACK_URL and AUTH0_CALLBACK_URL when deploying.`,
    });
  } else if (!validateAbsoluteUrl(redirectOverride)) {
    issues.push({
      level: 'error',
      envKey: 'VITE_AUTH0_CALLBACK_URL',
      message:
        `VITE_AUTH0_CALLBACK_URL must be an absolute URL (e.g., https://app.example.com${AUTH0_CALLBACK_PATH}).`,
    });
  }

  const logoutReturnTo = logoutOverride ?? stripTrailingSlash(origin);
  if (!logoutOverride) {
    issues.push({
      level: 'warning',
      envKey: 'VITE_AUTH0_LOGOUT_URL',
      message: `No logout return URL configured; defaulting to ${logoutReturnTo}. Configure VITE_AUTH0_LOGOUT_URL and update backend logout return settings to control post-logout redirects.`,
    });
  } else if (!validateAbsoluteUrl(logoutOverride)) {
    issues.push({
      level: 'error',
      envKey: 'VITE_AUTH0_LOGOUT_URL',
      message: 'VITE_AUTH0_LOGOUT_URL must be an absolute URL (e.g., https://app.example.com).',
    });
  }

  if (issues.some((issue) => issue.level === 'error')) {
    return { config: null, issues };
  }

  return {
    config: {
      domain: domain!,
      clientId: clientId!,
      audience,
      redirectUri,
      logoutReturnTo,
    },
    issues,
  };
};

export const getAuth0Config = (): Auth0Config | null =>
  validateAuth0Config().config;
